import { t } from "i18next";
import { useState } from "react";
import Popup from "reactjs-popup";

import { SearchInput } from "@src/components/ui-components/Inputs/SearchInput/SearchInput";
import styles from "./AdvancedSelectInput.module.css";

export interface Option {
  id: string;
  title: string;
  content?: {
    title: string;
    body: string;
  }[];
}

export interface Section {
  id: string;
  title: string;
  options: Option[];
  content?: {
    title: string;
    body: string;
  }[];
}

export interface ActionsPopoverAction {
  title: string;
  iconName?: string;
  trailingIconName?: string;
  iconColor?: string;
  onClick?: () => void;
  popoverMenu?: (trigger: JSX.Element) => JSX.Element;
}

type Props = {
  sections: Section[];
  trigger: JSX.Element | ((isOpen: boolean) => JSX.Element);
  didSelect: (option: Option, sectionID: string) => void;
  className?: string;
};

export const AdvancedSelectInput = ({ sections, trigger, didSelect, className }: Props) => {
  const [searchText, setSearchText] = useState("");
  const [selectedOption, setSelectedOption] = useState<Option | undefined>();

  const searchForText = (option: Option): boolean => {
    if (option.title && option.title.toLowerCase().includes(searchText.toLowerCase())) {
      return true;
    }
    if (option.content) {
      for (const content of option.content) {
        if (content.title.toLowerCase().includes(searchText.toLowerCase())) {
          return true;
        }
        if (content.body.toLowerCase().includes(searchText.toLowerCase())) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    <Popup
      on="click"
      position="top center"
      trigger={typeof trigger === "function" ? trigger(false) : <span>{trigger}</span>}
      arrow
      keepTooltipInside
      arrowStyle={{ zIndex: 14 }}
      overlayStyle={{ zIndex: 14 }}
      contentStyle={{ zIndex: 9999 }}
      mouseLeaveDelay={100}
      closeOnDocumentClick
    >
      <div className={styles.container}>
        <div className={styles.searchBar}>
          <SearchInput className={styles.searchInputField} value={searchText} onChange={(text) => setSearchText(text)} />
        </div>
        <div className={styles.content}>
          <ul className={styles.actionsList}>
            {sections.map((section, index) => {
              const filteredOptions = searchText.length > 0 ? section.options.filter((option) => searchForText(option)) : section.options;
              if (filteredOptions.length === 0) return null;
              return (
                <li key={index}>
                  <ul>
                    <li className={styles.sectionTitle}>{section.title}</li>
                    {filteredOptions.map((option) => {
                      return (
                        <li
                          key={option.id}
                          className={`${styles.selectable} ${selectedOption === option ? styles.selectedOption : ""}`}
                          onMouseEnter={() => setSelectedOption(option)}
                          onClick={() => {
                            didSelect(option, section.id);
                          }}
                        >
                          {option.title}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
          <div className={styles.info}>
            <h3 className={styles.contentTitle}>{selectedOption?.title}</h3>

            {selectedOption?.content && selectedOption.content.length > 0 ? (
              <ul>
                {selectedOption.content.map((content, index) => {
                  return (
                    <li key={index}>
                      {content.title.length > 0 || content.body.length > 0 ? (
                        <>
                          <p className={styles.contentSubtitle}>{content.title}</p>
                          <p>{content.body}</p>
                        </>
                      ) : (
                        <p style={{ fontStyle: "italic" }}>{t("main.noDescription")}1</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p style={{ fontStyle: "italic" }}>{t("main.noDescription")}</p>
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
};
